import React, { useEffect, useState } from 'react';
import { IInvoice } from 'internal/payments/Models';
import { ArrowForward, ChevronLeft, ChevronRight, Download } from '@mui/icons-material';
import { Colors } from 'library';
import { Card, Chip, Divider, EmptyText, IconButton, Link, Row, Select, Stack, Text, Title } from 'library/ui';
import { PagingResult } from 'models';
import { client, consoleLogInDev, download, formatCurrency } from 'shared';
import { formatMonthDay, formatMonthDayYear } from 'shared/Dates';
import { addDays } from 'date-fns';
import { useUserContext } from 'UserContext';

interface DuePaymentsProps {
    showViewAll?: boolean;
    pageSize?: number;
    maxRows?: number;
}

export default function DuePayments({ showViewAll = true, pageSize = 5, maxRows }: DuePaymentsProps) {
    const isPreview = maxRows !== undefined;
    const [pageNumber, setPageNumber] = useState(1);
    const [selectedLocation, setSelectedLocation] = useState('all');
    const [selectedDueDate, setSelectedDueDate] = useState('all');
    const [dueDates, setDueDates] = useState<string[]>([]);
    const [dueInvoices, setDueInvoices] = useState<PagingResult<IInvoice>>({
        count: 0,
        per_page: 0,
        num_pages: 0,
        results: [],
    });

    const { user } = useUserContext();

    const locations = user?.address.filter((x) => x.business_location).map((x) => x.business_location);
    const showLocationFilter = (locations?.length ?? 0) > 1;
    const includePending = !user?.is_manual_payer;

    useEffect(() => {
        getDuePayments(pageNumber);
    }, []);

    useEffect(() => {
        if (isPreview) return;
        client('payment/api/invoice/recent-due-dates/')
            .then((response: string[]) => setDueDates(response))
            .catch(consoleLogInDev);
    }, []);

    function getDuePayments(page: number, locationId = 'all', dueDate = 'all') {
        const locationParam = locationId === 'all' ? '' : locationId;
        const dueDateParam = dueDate === 'all' ? '' : dueDate;
        client(
            `payment/api/invoice/?type=recent&page=${page}&size=${pageSize}&location_id=${locationParam}&due_date=${dueDateParam}&include_pending=${includePending}`,
        )
            .then((response: PagingResult<IInvoice>) => {
                setDueInvoices(response);
            })
            .catch(consoleLogInDev);
    }

    function onDownloadClick(invoiceId: number) {
        download(`payment/api/invoice/${invoiceId}/download/`).catch((error) => {
            consoleLogInDev(error);
        });
    }

    function goToPage(page: number) {
        setPageNumber(page);
        getDuePayments(page, selectedLocation, selectedDueDate);
    }

    function onLocationChange(value: string) {
        const locationValue = String(value);
        setPageNumber(1);
        setSelectedLocation(locationValue);
        getDuePayments(1, locationValue, selectedDueDate);
    }

    function onDueDateChange(value: string) {
        const dueDateValue = String(value);
        setPageNumber(1);
        setSelectedDueDate(dueDateValue);
        getDuePayments(1, selectedLocation, dueDateValue);
    }

    function getWeekText(billDate: string) {
        const parsedBillDate = new Date(billDate);
        const weekStart = addDays(parsedBillDate, -6);

        return `${formatMonthDay(weekStart)}-${formatMonthDayYear(parsedBillDate)}`;
    }

    function getAmountText(invoice: IInvoice, isPaid: boolean) {
        if (isPaid) {
            return formatCurrency(invoice.amount);
        }

        const amountPaid = invoice.amount_paid;
        if (amountPaid) {
            const unpaidAmount = invoice.amount - amountPaid;
            return `${formatCurrency(invoice.amount)} (${formatCurrency(unpaidAmount)} owed)`;
        }

        return formatCurrency(invoice.amount);
    }

    const flatInvoices = dueInvoices.results.map((invoice) => ({ invoice, isPaid: !!invoice.paid_date }));
    const displayedInvoices = isPreview ? flatInvoices.slice(0, maxRows) : flatInvoices;

    const start = dueInvoices.count === 0 ? 0 : (pageNumber - 1) * dueInvoices.per_page + 1;
    const end = Math.min(pageNumber * dueInvoices.per_page, dueInvoices.count);
    const hasPrev = pageNumber > 1;
    const hasNext = pageNumber < dueInvoices.num_pages;

    return (
        <Card padding="lg" fullHeight>
            <Stack gap="md">
                <Row justify="between" align="center">
                    <Title size="h2">Recent Invoices</Title>
                    {showViewAll && (
                        <Link to="/payments/invoices">
                            View all <ArrowForward sx={{ fontSize: 16 }} />
                        </Link>
                    )}
                </Row>
                {!isPreview && (showLocationFilter || dueDates.length > 0) && (
                    <Row justify="end" gap="sm" wrap>
                        {showLocationFilter && (
                            <Select
                                value={selectedLocation}
                                options={[
                                    { key: 'all', value: 'All locations' },
                                    ...(locations?.map((b) => ({ key: b!.id.toString(), value: b!.name })) || []),
                                ]}
                                onChange={onLocationChange}
                            />
                        )}
                        {dueDates.length > 0 && (
                            <Select
                                value={selectedDueDate}
                                options={[
                                    { key: 'all', value: 'All due dates' },
                                    ...dueDates.map((dueDate) => ({
                                        key: dueDate,
                                        value: `Due ${formatMonthDayYear(dueDate.replaceAll('-', '/'))}`,
                                    })),
                                ]}
                                onChange={onDueDateChange}
                            />
                        )}
                    </Row>
                )}
                {flatInvoices.length === 0 ? (
                    <EmptyText>No recent invoices</EmptyText>
                ) : (
                    <Stack gap="sm">
                        {displayedInvoices.map(({ invoice, isPaid }) => (
                            <Card key={invoice.id} padding="sm" tone="muted">
                                <Row justify="between" align="center" gap="md">
                                    <Stack gap="none">
                                        <Text weight="bold">Invoice {invoice.id}</Text>
                                        <Text size="sm" tone="secondary">
                                            {invoice.bill_date
                                                ? `Week of ${getWeekText(invoice.bill_date)}`
                                                : 'Current week'}
                                        </Text>
                                        {invoice.address?.business_location?.name && (
                                            <Text size="sm" tone="secondary">
                                                {invoice.address.business_location.name}
                                            </Text>
                                        )}
                                        {invoice.due_date && (
                                            <Text size="sm" tone="secondary">
                                                Due {formatMonthDayYear(invoice.due_date.replaceAll('-', '/'))}
                                            </Text>
                                        )}
                                    </Stack>
                                    <Row gap="sm" align="center">
                                        <Text weight="bold">{getAmountText(invoice, isPaid)}</Text>
                                        <Chip
                                            variant="filled"
                                            bgColor={isPaid ? Colors.successBackground : Colors.warningBackground}
                                            textColor={isPaid ? Colors.success : Colors.warning}
                                        >
                                            {isPaid ? 'Paid' : 'Unpaid'}
                                        </Chip>
                                        <IconButton
                                            icon={Download}
                                            tooltip="Download"
                                            onClick={() => onDownloadClick(invoice.id)}
                                        />
                                    </Row>
                                </Row>
                            </Card>
                        ))}
                    </Stack>
                )}
                {!isPreview && dueInvoices.count > 0 && (
                    <Stack gap="sm">
                        <Divider />
                        <Row justify="between" align="center">
                            <Text size="sm" tone="secondary">
                                Showing {start}–{end} of {dueInvoices.count}
                            </Text>
                            <Row gap="sm" align="center">
                                <IconButton
                                    icon={ChevronLeft}
                                    disabled={!hasPrev}
                                    onClick={() => goToPage(pageNumber - 1)}
                                />
                                <IconButton
                                    icon={ChevronRight}
                                    disabled={!hasNext}
                                    onClick={() => goToPage(pageNumber + 1)}
                                />
                            </Row>
                        </Row>
                    </Stack>
                )}
            </Stack>
        </Card>
    );
}
